import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { MonacoCodeEditor } from '@/components/MonacoEditor';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useCreatePrompt } from '@/hooks/useCreatePrompt';

const promptSchema = z.object({
  promptName: z.string().min(1, 'Prompt name is required'),
  serviceName: z.string().min(1, 'Service name is required'),
  customer: z.string().optional(),
  template: z.string().min(1, 'Template is required'),
  responseSchema: z.string().optional(),
});

type PromptFormData = z.infer<typeof promptSchema>;

interface AddPromptFormProps {
  open: boolean;
  onClose: () => void;
}

export function AddPromptForm({ open, onClose }: AddPromptFormProps) {
  const { user } = useAuth();
  const contextualCustomerId = user?.customer?.id;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<PromptFormData>({
    resolver: zodResolver(promptSchema),
    defaultValues: {
      promptName: '',
      serviceName: '',
      customer: contextualCustomerId ? String(contextualCustomerId) : 'global',
      template: '',
      responseSchema: '',
    },
  });

  const { createPrompt, isLoading } = useCreatePrompt();

  async function onSubmit(data: PromptFormData) {
    try {
      // Parse responseSchema se existir
      let parsedResponseSchema: Record<string, any> | undefined;
      if (data.responseSchema && data.responseSchema.trim()) {
        try {
          parsedResponseSchema = JSON.parse(data.responseSchema);
        } catch (e) {
          setError('responseSchema', {
            message: 'Invalid JSON format',
          });
          return;
        }
      }

      const selectedCustomerId = data.customer
        ? Number.parseInt(data.customer, 10)
        : Number.NaN;
      const parsedCustomerId =
        contextualCustomerId ??
        (Number.isFinite(selectedCustomerId) && selectedCustomerId > 0
          ? selectedCustomerId
          : undefined);

      const promptData = {
        promptName: data.promptName,
        serviceName: data.serviceName,
        template: data.template,
        customerId: parsedCustomerId,
        responseSchema: parsedResponseSchema,
        main: false,
      };

      await createPrompt(promptData);

      toast.success('Prompt created successfully!');
      reset();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to create prompt. Please try again.',
      );
    }
  }

  const placeholderText = `Enter your prompt template here...

Example:
Hello {{user_name}}, 
Your order {{order_id}} is ready!

Use double curly braces for variables.`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Add Prompt</DialogTitle>
          <DialogDescription>
            Fill in the details for the new prompt in lower case.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <FormGroup
                error={{ message: errors.serviceName?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="serviceName">
                  Service Name
                </RequiredLabel>
                <Controller
                  name="serviceName"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} placeholder="Enter service name" />
                  )}
                />
              </FormGroup>

              <FormGroup
                error={{ message: errors.promptName?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="promptName">Prompt Name</RequiredLabel>
                <Controller
                  name="promptName"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} placeholder="Enter prompt name" />
                  )}
                />
              </FormGroup>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <FormGroup>
                <label htmlFor="customer" className="text-sm font-medium">
                  Customer (Optional)
                </label>
                <Controller
                  name="customer"
                  control={control}
                  render={({ field }) => (
                    <CustomerSelect
                      value={
                        contextualCustomerId
                          ? String(contextualCustomerId)
                          : (field.value ?? 'global')
                      }
                      onValueChange={field.onChange}
                      additionalOptions={[
                        { value: 'global', label: 'Global (sem customer)' },
                      ]}
                      disabled={Boolean(contextualCustomerId)}
                    />
                  )}
                />
              </FormGroup>
            </div>

            <Tabs defaultValue="template" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="template" className="flex-1">
                  Template
                </TabsTrigger>
                <TabsTrigger value="schema" className="flex-1">
                  Response Schema
                </TabsTrigger>
              </TabsList>

              <TabsContent value="template">
                <FormGroup
                  error={{ message: errors.template?.message, font: 'sm' }}
                >
                  <RequiredLabel htmlFor="template">Template</RequiredLabel>
                  <Controller
                    name="template"
                    control={control}
                    render={({ field }) => (
                      <MonacoCodeEditor
                        value={field.value}
                        placeholder={placeholderText}
                        onChange={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </FormGroup>
              </TabsContent>

              <TabsContent value="schema">
                <FormGroup
                  error={{
                    message: errors.responseSchema?.message,
                    font: 'sm',
                  }}
                >
                  <label
                    htmlFor="responseSchema"
                    className="text-sm font-medium"
                  >
                    Response Schema (JSON)
                  </label>
                  <Controller
                    name="responseSchema"
                    control={control}
                    render={({ field }) => (
                      <MonacoCodeEditor
                        value={field.value || ''}
                        placeholder={`{\n  "type": "object",\n  "properties": {\n    "name": {\n      "type": "string"\n    }\n  }\n}`}
                        onChange={(val) => field.onChange(val)}
                        language="json"
                      />
                    )}
                  />
                </FormGroup>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Prompt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
